import { Box, Divider, IconButton, Paper, Tooltip } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import * as Sentry from '@sentry/react';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IcomxResourceDescriptor,
  useEnsureIcomxResourceLink,
  useIcomxEmbedConfig,
} from 'src/services/api/icomx';
import useNotiMessage from 'src/hooks/useNotiMessage';
import useObjectState from 'src/hooks/useObjectState';
import { getIcomxApiErrorMessage, getIcomxBridgeErrorMessage } from './error-utils';
import IcomxCommunicationIcon from './IcomxCommunicationIcon';
import useIcomxBridge from './useIcomxBridge';
import { IcomxChatContext } from './IcomxChatContext';
import { IcomxSecurityRejection } from './types';

const ICOMX_IFRAME_SANDBOX =
  'allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox';
const OPEN_CHAT_CONVERSATION = 'comx.host.open_last_conversation' as const;
const UNVERIFIED_TELECOM_MESSAGE =
  'The selected contact information could not be verified. Refresh the record and try again.';
const UNRECONCILED_CONTACT_MESSAGE =
  'iComX could not verify the contact mapping. Refresh the record and try again.';
export const ICOMX_EXPANDED_WIDTH = '50vw';
export const ICOMX_MINIMIZED_WIDTH = '96px';
export const ICOMX_EXPANDED_HEIGHT = '70vh';
const ICOMX_VIEWPORT_MARGIN = 24;
const ICOMX_COMPACT_VIEWPORT_MARGIN = 16;
const ICOMX_MAXIMIZED_VIEWPORT_MARGIN = 48;
const ICOMX_MAXIMIZED_WIDTH_RATIO = 0.82;
const ICOMX_MAXIMIZED_HEIGHT_RATIO = 0.8;
const ICOMX_HEADER_HEIGHT = 56;
const ICOMX_MINIMIZED_MIN_WIDTH = 96;
const ICOMX_MIN_WIDTH = 220;
const ICOMX_MIN_EXPANDED_WIDTH = 320;
const ICOMX_MIN_EXPANDED_BODY_HEIGHT = 280;
const ICOMX_RESIZE_STEP = 24;
const ICOMX_DRAG_THRESHOLD = 4;
const ICOMX_DIRECTION_SWITCH_THRESHOLD = 96;
const ICOMX_POSITION_STORAGE_KEY = 'icomx-widget-position';
const ICOMX_SIZE_STORAGE_KEY = 'icomx-widget-size';
const MISSING_CONTACT_UUID_MESSAGE =
  'iComx did not return a resource chat mapping. Please try again.';
const ICOMX_READY_TIMEOUT_MS = 15000;
const ICOMX_READY_TIMEOUT_MESSAGE =
  'iComx chat did not finish loading. Please try again after the chat finishes booting or signing in.';
const ICOMX_DRAG_CLICK_SUPPRESSION_MS = 120;

type ViewportBucket = 'mobile' | 'tablet' | 'desktop';

interface IcomxChatProviderProps {
  children: ReactNode;
  sessionKey: string | null;
}

interface WidgetState {
  isExpanded: boolean;
  isMaximized: boolean;
  activeIntent: typeof OPEN_CHAT_CONVERSATION | null;
  pendingContactUuid: string | null;
  pendingSelectedTelecom: IcomxResourceDescriptor['selectedTelecom'] | null;
  openDirection: 'up' | 'down';
}

interface ViewportSize {
  width: number;
  height: number;
}

interface HeaderPosition {
  left: number;
  top: number;
}

interface WidgetSize {
  width: number;
  height: number;
}

interface WidgetLayout {
  headerLeft: number;
  headerTop: number;
  panelTop: number;
  width: number;
  height: number;
  openDirection: 'up' | 'down';
}

interface DragState {
  pointerId: number;
  pointerTarget: HTMLDivElement;
  originLeft: number;
  originTop: number;
  originX: number;
  originY: number;
  moved: boolean;
}

interface DragListeners {
  move: (event: PointerEvent) => void;
  up: (event: PointerEvent) => void;
  cancel: (event: PointerEvent) => void;
  lostCapture: (event: PointerEvent) => void;
  blur: () => void;
  target: HTMLDivElement;
}

interface ResizeState {
  pointerId: number;
  pointerTarget: HTMLDivElement;
  originWidth: number;
  originHeight: number;
  originX: number;
  originY: number;
  openDirection: 'up' | 'down';
}

interface ResizeListeners {
  move: (event: PointerEvent) => void;
  up: (event: PointerEvent) => void;
  cancel: (event: PointerEvent) => void;
  lostCapture: (event: PointerEvent) => void;
  blur: () => void;
  target: HTMLDivElement;
}

interface StoredHeaderPosition {
  left: number;
  top: number;
}

interface StoredHeaderPositions {
  mobile?: StoredHeaderPosition;
  tablet?: StoredHeaderPosition;
  desktop?: StoredHeaderPosition;
}

interface StoredWidgetSizes {
  mobile?: WidgetSize;
  tablet?: WidgetSize;
  desktop?: WidgetSize;
}

export function resolveIcomxIframeOrigin(
  iframeOrigin?: string | null,
  iframeUrl?: string | null
): string | null {
  const candidates = [iframeOrigin, iframeUrl];

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    try {
      const normalizedUrl = new URL(candidate);

      if (normalizedUrl.origin && normalizedUrl.origin !== 'null') {
        return normalizedUrl.origin;
      }
    } catch (error) {
      if (typeof candidate === 'string' && /^https?:\/\//i.test(candidate)) {
        const trimmedCandidate = candidate.replace(/\/+$/, '');

        if (trimmedCandidate) {
          return trimmedCandidate;
        }
      }
    }
  }

  return null;
}

export function buildIcomxIframeSrc(iframeUrl?: string | null): string | null {
  if (!iframeUrl) {
    return null;
  }

  try {
    const resolvedUrl = new URL(iframeUrl);

    if (typeof window !== 'undefined' && window.location.origin) {
      resolvedUrl.searchParams.set('hostOrigin', window.location.origin);
    }

    return resolvedUrl.toString();
  } catch (error) {
    return iframeUrl;
  }
}

function clamp(value: number, min: number, max: number) {
  if (max < min) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
}

function resolveViewportSize(): ViewportSize {
  if (typeof window === 'undefined') {
    return { width: 1280, height: 720 };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

function getDefaultHeaderPosition(viewport: ViewportSize): HeaderPosition {
  return {
    left: ICOMX_VIEWPORT_MARGIN,
    top: viewport.height - ICOMX_HEADER_HEIGHT - ICOMX_VIEWPORT_MARGIN,
  };
}

export function getViewportBucket(viewport: ViewportSize): ViewportBucket {
  if (viewport.width < 768) {
    return 'mobile';
  }

  if (viewport.width < 1280) {
    return 'tablet';
  }

  return 'desktop';
}

function readStoredHeaderPositions(): StoredHeaderPositions {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const rawValue = window.localStorage.getItem(ICOMX_POSITION_STORAGE_KEY);

    if (!rawValue) {
      return {};
    }

    const parsedValue = JSON.parse(rawValue) as
      | StoredHeaderPositions
      | Partial<StoredHeaderPosition>;

    if ('left' in parsedValue || 'top' in parsedValue) {
      if (typeof parsedValue.left === 'number' && typeof parsedValue.top === 'number') {
        return {
          desktop: {
            left: parsedValue.left,
            top: parsedValue.top,
          },
        };
      }

      return {};
    }

    return parsedValue as StoredHeaderPositions;
  } catch (error) {
    console.warn('Unable to read stored iComx widget position', error);
    return {};
  }
}

function readStoredHeaderPosition(bucket: ViewportBucket): StoredHeaderPosition | null {
  const storedPositions = readStoredHeaderPositions();
  const storedPosition = storedPositions[bucket];

  if (!storedPosition) {
    return null;
  }

  if (typeof storedPosition.left !== 'number' || typeof storedPosition.top !== 'number') {
    return null;
  }

  return storedPosition;
}

function writeStoredHeaderPosition(position: HeaderPosition, bucket: ViewportBucket) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const storedPositions = readStoredHeaderPositions();

    window.localStorage.setItem(
      ICOMX_POSITION_STORAGE_KEY,
      JSON.stringify({
        ...storedPositions,
        [bucket]: position,
      })
    );
  } catch (error) {
    console.warn('Unable to persist iComx widget position', error);
  }
}

function readStoredWidgetSizes(): StoredWidgetSizes {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const rawValue = window.localStorage.getItem(ICOMX_SIZE_STORAGE_KEY);

    if (!rawValue) {
      return {};
    }

    return JSON.parse(rawValue) as StoredWidgetSizes;
  } catch (error) {
    console.warn('Unable to read stored iComx widget size', error);
    return {};
  }
}

function readStoredWidgetSize(bucket: ViewportBucket): WidgetSize | null {
  const storedSize = readStoredWidgetSizes()[bucket];

  if (
    !storedSize ||
    typeof storedSize.width !== 'number' ||
    typeof storedSize.height !== 'number' ||
    !Number.isFinite(storedSize.width) ||
    !Number.isFinite(storedSize.height)
  ) {
    return null;
  }

  return storedSize;
}

function writeStoredWidgetSize(size: WidgetSize, bucket: ViewportBucket) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const storedSizes = readStoredWidgetSizes();

    window.localStorage.setItem(
      ICOMX_SIZE_STORAGE_KEY,
      JSON.stringify({
        ...storedSizes,
        [bucket]: size,
      })
    );
  } catch (error) {
    console.warn('Unable to persist iComx widget size', error);
  }
}

function getViewportUnits(value: string, viewport: number) {
  if (value.endsWith('vw') || value.endsWith('vh')) {
    return (Number.parseFloat(value) / 100) * viewport;
  }

  return Number.parseFloat(value);
}

function getDefaultDesktopWidgetSize(viewport: ViewportSize): WidgetSize {
  const availableWidth = Math.max(
    viewport.width - ICOMX_VIEWPORT_MARGIN * 2,
    ICOMX_MIN_WIDTH
  );
  const availableHeight = Math.max(
    ICOMX_HEADER_HEIGHT,
    viewport.height - ICOMX_VIEWPORT_MARGIN * 2
  );

  return {
    width: clamp(
      getViewportUnits(ICOMX_EXPANDED_WIDTH, viewport.width),
      Math.min(ICOMX_MIN_EXPANDED_WIDTH, availableWidth),
      availableWidth
    ),
    height: clamp(
      getViewportUnits(ICOMX_EXPANDED_HEIGHT, viewport.height),
      Math.min(ICOMX_HEADER_HEIGHT + ICOMX_MIN_EXPANDED_BODY_HEIGHT, availableHeight),
      availableHeight
    ),
  };
}

function capturePointer(target: HTMLDivElement, pointerId: number) {
  if (typeof target.setPointerCapture !== 'function') {
    return;
  }

  try {
    target.setPointerCapture(pointerId);
  } catch {
    // The global listeners and buttons-state fallback still complete the interaction.
  }
}

function releasePointer(target: HTMLDivElement, pointerId: number) {
  if (
    typeof target.hasPointerCapture !== 'function' ||
    typeof target.releasePointerCapture !== 'function'
  ) {
    return;
  }

  try {
    if (target.hasPointerCapture(pointerId)) {
      target.releasePointerCapture(pointerId);
    }
  } catch {
    // The pointer may already have been released by the browser.
  }
}

export function getWidgetLayout(
  position: HeaderPosition,
  viewport: ViewportSize,
  isExpanded: boolean,
  openDirection: 'up' | 'down',
  requestedSize?: WidgetSize,
  isMaximized = false
): WidgetLayout {
  const viewportBucket = getViewportBucket(viewport);
  const expandedViewportMargin =
    viewportBucket === 'mobile' ? ICOMX_COMPACT_VIEWPORT_MARGIN : ICOMX_VIEWPORT_MARGIN;
  const availableViewportWidth = Math.max(
    viewport.width - expandedViewportMargin * 2,
    ICOMX_MINIMIZED_MIN_WIDTH
  );
  const minimizedWidth = clamp(
    getViewportUnits(ICOMX_MINIMIZED_WIDTH, viewport.width),
    ICOMX_MINIMIZED_MIN_WIDTH,
    availableViewportWidth
  );
  const availableExpandedViewportWidth = Math.max(
    viewport.width - expandedViewportMargin * 2,
    ICOMX_MIN_WIDTH
  );
  const availableExpandedViewportHeight = Math.max(
    ICOMX_HEADER_HEIGHT,
    viewport.height - expandedViewportMargin * 2
  );
  const responsiveWidth =
    viewportBucket === 'desktop'
      ? requestedSize?.width ?? getViewportUnits(ICOMX_EXPANDED_WIDTH, viewport.width)
      : availableExpandedViewportWidth;
  const expandedWidth = clamp(
    responsiveWidth,
    Math.min(ICOMX_MIN_EXPANDED_WIDTH, availableExpandedViewportWidth),
    availableExpandedViewportWidth
  );
  const width = isExpanded ? expandedWidth : minimizedWidth;
  const maxClampedWidth = expandedWidth;
  const headerLeft = clamp(
    position.left,
    expandedViewportMargin,
    viewport.width - maxClampedWidth - expandedViewportMargin
  );

  if (!isExpanded) {
    const headerTop = clamp(
      position.top,
      expandedViewportMargin,
      viewport.height - ICOMX_HEADER_HEIGHT - expandedViewportMargin
    );

    return {
      headerLeft,
      headerTop,
      panelTop: headerTop,
      width,
      height: ICOMX_HEADER_HEIGHT,
      openDirection,
    };
  }

  if (isMaximized && viewportBucket === 'desktop') {
    const width = clamp(
      viewport.width * ICOMX_MAXIMIZED_WIDTH_RATIO,
      Math.min(ICOMX_MIN_EXPANDED_WIDTH, availableExpandedViewportWidth),
      Math.max(ICOMX_MIN_EXPANDED_WIDTH, viewport.width - ICOMX_MAXIMIZED_VIEWPORT_MARGIN * 2)
    );
    const height = clamp(
      viewport.height * ICOMX_MAXIMIZED_HEIGHT_RATIO,
      Math.min(
        ICOMX_HEADER_HEIGHT + ICOMX_MIN_EXPANDED_BODY_HEIGHT,
        availableExpandedViewportHeight
      ),
      Math.max(
        ICOMX_HEADER_HEIGHT,
        viewport.height - ICOMX_MAXIMIZED_VIEWPORT_MARGIN * 2
      )
    );
    const headerLeft = clamp(
      position.left,
      ICOMX_MAXIMIZED_VIEWPORT_MARGIN,
      viewport.width - width - ICOMX_MAXIMIZED_VIEWPORT_MARGIN
    );
    const headerTop = clamp(
      position.top,
      ICOMX_MAXIMIZED_VIEWPORT_MARGIN,
      viewport.height - height - ICOMX_MAXIMIZED_VIEWPORT_MARGIN
    );

    return {
      headerLeft,
      headerTop,
      panelTop: headerTop,
      width,
      height,
      openDirection: 'down',
    };
  }

  const responsiveHeight =
    viewportBucket === 'desktop'
      ? requestedSize?.height ?? getViewportUnits(ICOMX_EXPANDED_HEIGHT, viewport.height)
      : availableExpandedViewportHeight;
  const desiredHeight = clamp(
    responsiveHeight,
    Math.min(
      ICOMX_HEADER_HEIGHT + ICOMX_MIN_EXPANDED_BODY_HEIGHT,
      availableExpandedViewportHeight
    ),
    availableExpandedViewportHeight
  );
  const headerTop =
    openDirection === 'down'
      ? clamp(
          position.top,
          expandedViewportMargin,
          viewport.height - desiredHeight - expandedViewportMargin
        )
      : clamp(
          position.top,
          desiredHeight - ICOMX_HEADER_HEIGHT + expandedViewportMargin,
          viewport.height - ICOMX_HEADER_HEIGHT - expandedViewportMargin
        );
  const height = desiredHeight;
  const panelTop = openDirection === 'down' ? headerTop : headerTop + ICOMX_HEADER_HEIGHT - height;

  return {
    headerLeft,
    headerTop,
    panelTop,
    width,
    height,
    openDirection,
  };
}

export function getPreferredOpenDirection(
  position: HeaderPosition,
  viewport: ViewportSize,
  currentDirection: 'up' | 'down'
) {
  const headerTop = clamp(
    position.top,
    ICOMX_VIEWPORT_MARGIN,
    viewport.height - ICOMX_HEADER_HEIGHT - ICOMX_VIEWPORT_MARGIN
  );
  const availableDown = viewport.height - ICOMX_VIEWPORT_MARGIN - headerTop;
  const availableUp = headerTop + ICOMX_HEADER_HEIGHT - ICOMX_VIEWPORT_MARGIN;

  if (currentDirection === 'down') {
    return availableUp > availableDown + ICOMX_DIRECTION_SWITCH_THRESHOLD ? 'up' : 'down';
  }

  return availableDown > availableUp + ICOMX_DIRECTION_SWITCH_THRESHOLD ? 'down' : 'up';
}

function logSecurityRejection(rejection: IcomxSecurityRejection) {
  console.warn('iComX bridge security rejection', rejection.reason, rejection.details);
  Sentry.captureMessage(`iComX bridge security rejection: ${rejection.reason}`);
}

export default function IcomxChatProvider({ children, sessionKey }: IcomxChatProviderProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const lastBridgeEventKeyRef = useRef<string | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const dragListenersRef = useRef<DragListeners | null>(null);
  const dragFrameRef = useRef<number | null>(null);
  const pendingPositionRef = useRef<HeaderPosition | null>(null);
  const resizeStateRef = useRef<ResizeState | null>(null);
  const resizeListenersRef = useRef<ResizeListeners | null>(null);
  const resizeFrameRef = useRef<number | null>(null);
  const pendingSizeRef = useRef<WidgetSize | null>(null);
  const preMaximizePositionRef = useRef<HeaderPosition | null>(null);
  const queuedLaunchTimeoutRef = useRef<number | null>(null);
  const suppressToggleUntilRef = useRef(0);
  const finishDraggingRef = useRef<() => void>(() => undefined);
  const finishResizingRef = useRef<() => void>(() => undefined);
  const { showErrorMessage, showWarningMessage } = useNotiMessage();
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [viewportSize, setViewportSize] = useState<ViewportSize>(() => resolveViewportSize());
  const [headerPosition, setHeaderPosition] = useState<HeaderPosition>(() => {
    const viewport = resolveViewportSize();
    const viewportBucket = getViewportBucket(viewport);
    const storedPosition = readStoredHeaderPosition(viewportBucket);

    return storedPosition ?? getDefaultHeaderPosition(viewport);
  });
  const [widgetSize, setWidgetSize] = useState<WidgetSize>(() => {
    const viewport = resolveViewportSize();
    const storedSize = readStoredWidgetSize('desktop');

    return storedSize ?? getDefaultDesktopWidgetSize(viewport);
  });

  const [widgetState, updateWidgetState] = useObjectState<WidgetState>({
    isExpanded: false,
    isMaximized: false,
    activeIntent: null,
    pendingContactUuid: null,
    pendingSelectedTelecom: null,
    openDirection: 'up',
  });

  const { data: embedConfig, isError: isConfigError } = useIcomxEmbedConfig({
    retry: false,
    staleTime: 0,
    refetchInterval: (data) => data?.enabled ? 30_000 : false,
    refetchOnWindowFocus: true,
  }, sessionKey);

  const iframeOrigin = useMemo(
    () => resolveIcomxIframeOrigin(embedConfig?.iframeOrigin, embedConfig?.iframeUrl),
    [embedConfig?.iframeOrigin, embedConfig?.iframeUrl]
  );
  const iframeSrc = useMemo(
    () => buildIcomxIframeSrc(embedConfig?.iframeUrl),
    [embedConfig?.iframeUrl]
  );

  const ensureIcomxResourceLink = useEnsureIcomxResourceLink();

  const onBridgeError = useCallback((error: Error) => {
    console.error('iComX bridge failure', error);
    Sentry.captureException(error);
  }, []);

  const hasIframeConfig = !!iframeSrc && !!iframeOrigin;
  const isEnabled = !!sessionKey && !isConfigError && !!embedConfig?.enabled && hasIframeConfig;
  const runtimeKey = isEnabled ? sessionKey : null;
  const runtime = useMemo(() => ({ key: runtimeKey }), [runtimeKey]);
  const activeRuntime = useRef(runtime);
  activeRuntime.current = runtime;

  const bridge = useIcomxBridge({
    iframeRef,
    iframeOrigin: isEnabled ? iframeOrigin : null,
    contextKey: runtimeKey,
    onBridgeError,
    onSecurityRejection: logSecurityRejection,
  });

  const isLoading = !!sessionKey && !isConfigError && embedConfig === undefined;
  const viewportBucket = useMemo(() => getViewportBucket(viewportSize), [viewportSize]);
  const widgetLayout = useMemo(
    () =>
      getWidgetLayout(
        headerPosition,
        viewportSize,
        widgetState.isExpanded,
        widgetState.openDirection,
        widgetSize,
        widgetState.isMaximized
      ),
    [
      headerPosition,
      viewportSize,
      widgetSize,
      widgetState.isExpanded,
      widgetState.isMaximized,
      widgetState.openDirection,
    ]
  );

  useEffect(() => {
    const handleResize = () => {
      setViewportSize(resolveViewportSize());
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (widgetState.isMaximized) {
      return;
    }

    setHeaderPosition((currentPosition) => {
      const nextLayout = getWidgetLayout(
        currentPosition,
        viewportSize,
        widgetState.isExpanded,
        widgetState.openDirection,
        widgetSize,
        widgetState.isMaximized
      );

      if (
        nextLayout.headerLeft === currentPosition.left &&
        nextLayout.headerTop === currentPosition.top
      ) {
        return currentPosition;
      }

      return {
        left: nextLayout.headerLeft,
        top: nextLayout.headerTop,
      };
    });
  }, [
    viewportSize,
    widgetSize,
    widgetState.isExpanded,
    widgetState.isMaximized,
    widgetState.openDirection,
  ]);

  useEffect(() => {
    if (widgetState.isMaximized) {
      return;
    }

    writeStoredHeaderPosition(headerPosition, viewportBucket);
  }, [headerPosition, viewportBucket, widgetState.isMaximized]);

  useEffect(() => {
    writeStoredWidgetSize(widgetSize, 'desktop');
  }, [widgetSize]);

  useEffect(() => {
    const storedPosition = readStoredHeaderPosition(viewportBucket);

    setHeaderPosition(storedPosition ?? getDefaultHeaderPosition(viewportSize));
  }, [viewportBucket, viewportSize]);

  useEffect(() => {
    if (!widgetState.isExpanded || widgetState.isMaximized || isDragging || isResizing) {
      return;
    }

    const preferredDirection = getPreferredOpenDirection(
      headerPosition,
      viewportSize,
      widgetState.openDirection
    );

    if (preferredDirection !== widgetState.openDirection) {
      updateWidgetState({ openDirection: preferredDirection });
    }
  }, [
    headerPosition,
    isDragging,
    isResizing,
    updateWidgetState,
    viewportSize,
    widgetState.isExpanded,
    widgetState.isMaximized,
    widgetState.openDirection,
  ]);

  useEffect(() => {
    if (!bridge.lastEvent) {
      return;
    }

    const eventKey = `${bridge.lastEvent.requestId}:${bridge.lastEvent.type}`;

    if (lastBridgeEventKeyRef.current === eventKey) {
      return;
    }

    lastBridgeEventKeyRef.current = eventKey;

    if (bridge.lastEvent.type === 'comx.iframe.popup_blocked') {
      showWarningMessage(
        'The iComX sign-in or provider popup was blocked. Allow popups and continue in the new window.'
      );
      return;
    }

    if (bridge.lastEvent.type === 'comx.iframe.popup_closed') {
      showWarningMessage(
        'The iComX sign-in or provider popup was closed before completion. Reopen the flow from iComX to continue.'
      );
    }
  }, [bridge.lastEvent, showWarningMessage]);

  const clearQueuedLaunchTimeout = useCallback(() => {
    if (queuedLaunchTimeoutRef.current === null) {
      return;
    }

    clearTimeout(queuedLaunchTimeoutRef.current);
    queuedLaunchTimeoutRef.current = null;
  }, []);

  useEffect(() => {
    clearQueuedLaunchTimeout();

    if (!widgetState.pendingContactUuid || bridge.isReady || !isEnabled) {
      return;
    }

    queuedLaunchTimeoutRef.current = window.setTimeout(() => {
      queuedLaunchTimeoutRef.current = null;
      showErrorMessage(ICOMX_READY_TIMEOUT_MESSAGE, { persist: false });
      updateWidgetState({
        activeIntent: null,
        pendingContactUuid: null,
        pendingSelectedTelecom: null,
      });
    }, ICOMX_READY_TIMEOUT_MS);

    return clearQueuedLaunchTimeout;
  }, [
    bridge.isReady,
    clearQueuedLaunchTimeout,
    isEnabled,
    showErrorMessage,
    updateWidgetState,
    widgetState.pendingContactUuid,
  ]);

  const handleIframeLoad = useCallback(() => {
    bridge.reset('The iComX iframe reloaded before the request completed');
    bridge.refreshContext();
  }, [bridge]);

  const executeOpenLastConversation = useCallback(
    async (
      contactUuid: string,
      selectedTelecom?: IcomxResourceDescriptor['selectedTelecom']
    ) => {
      if (widgetState.activeIntent || activeRuntime.current !== runtime) {
        return;
      }

      updateWidgetState({ activeIntent: OPEN_CHAT_CONVERSATION });

      try {
        await bridge.sendCommand(OPEN_CHAT_CONVERSATION, {
          contactUuid,
          fallbackToDirect: true,
          ...(selectedTelecom ? { selectedTelecom } : {}),
        });
      } catch (error) {
        if (activeRuntime.current !== runtime) return;
        showErrorMessage(getIcomxBridgeErrorMessage(error), { persist: false });
      } finally {
        if (activeRuntime.current === runtime) updateWidgetState({
          activeIntent: null,
          pendingContactUuid: null,
          pendingSelectedTelecom: null,
        });
      }
    },
    [bridge, runtime, showErrorMessage, updateWidgetState, widgetState.activeIntent]
  );

  useEffect(() => {
    if (
      !widgetState.pendingContactUuid ||
      widgetState.activeIntent ||
      !isEnabled ||
      !bridge.isReady
    ) {
      return;
    }

    void executeOpenLastConversation(
      widgetState.pendingContactUuid,
      widgetState.pendingSelectedTelecom ?? undefined
    );
  }, [
    bridge.isReady,
    executeOpenLastConversation,
    isEnabled,
    widgetState.activeIntent,
    widgetState.pendingContactUuid,
    widgetState.pendingSelectedTelecom,
  ]);

  const openResourceConversation = useCallback(
    async (resource: IcomxResourceDescriptor) => {
      if (!isEnabled || activeRuntime.current !== runtime || widgetState.activeIntent || widgetState.pendingContactUuid) {
        return;
      }

      updateWidgetState({
        isExpanded: true,
        isMaximized: false,
        activeIntent: OPEN_CHAT_CONVERSATION,
        openDirection: getPreferredOpenDirection(
          headerPosition,
          viewportSize,
          widgetState.openDirection
        ),
      });

      let linkPrepared = false;
      try {
        const link = await ensureIcomxResourceLink.mutateAsync(resource);
        if (activeRuntime.current !== runtime) return;
        linkPrepared = true;

        if (resource.selectedTelecom && link.selectedTelecomVerified !== true) {
          showErrorMessage(UNVERIFIED_TELECOM_MESSAGE, { persist: false });
          updateWidgetState({
            activeIntent: null,
            pendingContactUuid: null,
            pendingSelectedTelecom: null,
          });
          return;
        }

        if (!link.comxContactUuid) {
          showErrorMessage(MISSING_CONTACT_UUID_MESSAGE, { persist: false });
          updateWidgetState({
            activeIntent: null,
            pendingContactUuid: null,
            pendingSelectedTelecom: null,
          });
          return;
        }

        if (link.contactReconciled !== true) {
          showErrorMessage(UNRECONCILED_CONTACT_MESSAGE, { persist: false });
          updateWidgetState({
            activeIntent: null,
            pendingContactUuid: null,
            pendingSelectedTelecom: null,
          });
          return;
        }

        if (bridge.isReady) {
          await bridge.sendCommand(OPEN_CHAT_CONVERSATION, {
            contactUuid: link.comxContactUuid,
            fallbackToDirect: true,
            ...(resource.selectedTelecom
              ? { selectedTelecom: resource.selectedTelecom }
              : {}),
          });

          if (activeRuntime.current !== runtime) return;
          updateWidgetState({
            activeIntent: null,
            pendingContactUuid: null,
            pendingSelectedTelecom: null,
          });
          return;
        }

        updateWidgetState({
          activeIntent: null,
          pendingContactUuid: link.comxContactUuid,
          pendingSelectedTelecom: resource.selectedTelecom ?? null,
        });
      } catch (error) {
        if (activeRuntime.current !== runtime) return;
        const message = !linkPrepared
          ? getIcomxApiErrorMessage(
              error,
              `Unable to prepare the ${resource.resourceType.toLowerCase()} chat mapping`
            )
          : getIcomxBridgeErrorMessage(error);

        showErrorMessage(message, { persist: false });
        updateWidgetState({
          activeIntent: null,
          pendingContactUuid: null,
          pendingSelectedTelecom: null,
        });
      }
    },
    [
      bridge,
      ensureIcomxResourceLink,
      runtime,
      headerPosition,
      isEnabled,
      showErrorMessage,
      updateWidgetState,
      viewportSize,
      widgetState.activeIntent,
      widgetState.openDirection,
      widgetState.pendingContactUuid,
    ]
  );

  const toggleExpanded = useCallback(() => {
    updateWidgetState({
      isExpanded: !widgetState.isExpanded,
      isMaximized: false,
      openDirection: widgetState.isExpanded
        ? widgetState.openDirection
        : getPreferredOpenDirection(headerPosition, viewportSize, widgetState.openDirection),
    });
  }, [
    headerPosition,
    updateWidgetState,
    viewportSize,
    widgetState.isExpanded,
    widgetState.openDirection,
  ]);

  const toggleMaximized = useCallback(() => {
    finishDraggingRef.current();
    finishResizingRef.current();

    if (!widgetState.isMaximized) {
      const maximizedLayout = getWidgetLayout(
        { left: 0, top: 0 },
        viewportSize,
        true,
        'down',
        widgetSize,
        true
      );

      preMaximizePositionRef.current = headerPosition;
      setHeaderPosition({
        left: Math.round((viewportSize.width - maximizedLayout.width) / 2),
        top: Math.round((viewportSize.height - maximizedLayout.height) / 2),
      });
    } else if (preMaximizePositionRef.current) {
      setHeaderPosition(preMaximizePositionRef.current);
      preMaximizePositionRef.current = null;
    }

    updateWidgetState({
      isExpanded: true,
      isMaximized: !widgetState.isMaximized,
    });
  }, [
    headerPosition,
    updateWidgetState,
    viewportSize,
    widgetSize,
    widgetState.isMaximized,
  ]);

  const commitDraggedPosition = useCallback((nextPosition: HeaderPosition) => {
    setHeaderPosition(nextPosition);
    pendingPositionRef.current = null;
    dragFrameRef.current = null;
  }, []);

  const scheduleDraggedPosition = useCallback(
    (nextPosition: HeaderPosition) => {
      pendingPositionRef.current = nextPosition;

      if (dragFrameRef.current !== null) {
        return;
      }

      dragFrameRef.current = window.requestAnimationFrame(() => {
        if (pendingPositionRef.current) {
          commitDraggedPosition(pendingPositionRef.current);
        }
      });
    },
    [commitDraggedPosition]
  );

  const finishDragging = useCallback(() => {
    const activeDragState = dragStateRef.current;
    const didMove = !!dragStateRef.current?.moved;
    const finalPosition = pendingPositionRef.current ?? headerPosition;

    if (dragFrameRef.current !== null) {
      window.cancelAnimationFrame(dragFrameRef.current);
      dragFrameRef.current = null;
    }

    if (pendingPositionRef.current) {
      setHeaderPosition(pendingPositionRef.current);
      pendingPositionRef.current = null;
    }

    if (dragListenersRef.current) {
      window.removeEventListener('pointermove', dragListenersRef.current.move);
      window.removeEventListener('pointerup', dragListenersRef.current.up);
      window.removeEventListener('pointercancel', dragListenersRef.current.cancel);
      window.removeEventListener('blur', dragListenersRef.current.blur);
      dragListenersRef.current.target.removeEventListener(
        'lostpointercapture',
        dragListenersRef.current.lostCapture
      );
      dragListenersRef.current = null;
    }

    dragStateRef.current = null;

    if (activeDragState) {
      releasePointer(activeDragState.pointerTarget, activeDragState.pointerId);
    }

    setIsDragging(false);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';

    if (didMove && widgetState.isExpanded && !widgetState.isMaximized) {
      updateWidgetState({
        openDirection: getPreferredOpenDirection(
          finalPosition,
          resolveViewportSize(),
          widgetState.openDirection
        ),
      });
    }

    if (didMove) {
      suppressToggleUntilRef.current = Date.now() + ICOMX_DRAG_CLICK_SUPPRESSION_MS;
    }
  }, [
    headerPosition,
    updateWidgetState,
    widgetState.isExpanded,
    widgetState.isMaximized,
    widgetState.openDirection,
  ]);

  finishDraggingRef.current = finishDragging;

  const handleWindowPointerMove = useCallback(
    (event: PointerEvent) => {
      const dragState = dragStateRef.current;

      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }

      if (event.buttons === 0) {
        finishDraggingRef.current();
        return;
      }

      event.preventDefault();

      const deltaX = event.clientX - dragState.originX;
      const deltaY = event.clientY - dragState.originY;
      const nextPosition = {
        left: dragState.originLeft + deltaX,
        top: dragState.originTop + deltaY,
      };
      const nextLayout = getWidgetLayout(
        nextPosition,
        resolveViewportSize(),
        widgetState.isExpanded,
        widgetState.openDirection,
        widgetSize,
        widgetState.isMaximized
      );

      if (
        !dragState.moved &&
        (Math.abs(deltaX) > ICOMX_DRAG_THRESHOLD || Math.abs(deltaY) > ICOMX_DRAG_THRESHOLD)
      ) {
        dragStateRef.current = {
          ...dragState,
          moved: true,
        };
        setIsDragging(true);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
      }

      scheduleDraggedPosition({
        left: nextLayout.headerLeft,
        top: nextLayout.headerTop,
      });
    },
    [
      scheduleDraggedPosition,
      widgetSize,
      widgetState.isExpanded,
      widgetState.isMaximized,
      widgetState.openDirection,
    ]
  );

  const handleDragHandlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (
        event.button !== 0 ||
        viewportBucket !== 'desktop' ||
        isResizing
      ) {
        return;
      }

      event.preventDefault();
      finishDraggingRef.current();

      dragStateRef.current = {
        pointerId: event.pointerId,
        pointerTarget: event.currentTarget,
        originLeft: widgetLayout.headerLeft,
        originTop: widgetLayout.headerTop,
        originX: event.clientX,
        originY: event.clientY,
        moved: false,
      };

      capturePointer(event.currentTarget, event.pointerId);

      const moveListener = (nativeEvent: PointerEvent) => {
        handleWindowPointerMove(nativeEvent);
      };
      const upListener = (nativeEvent: PointerEvent) => {
        if (dragStateRef.current?.pointerId !== nativeEvent.pointerId) {
          return;
        }

        finishDraggingRef.current();
      };
      const cancelListener = (nativeEvent: PointerEvent) => {
        if (dragStateRef.current?.pointerId !== nativeEvent.pointerId) {
          return;
        }

        finishDraggingRef.current();
      };
      const lostCaptureListener = (nativeEvent: PointerEvent) => {
        if (dragStateRef.current?.pointerId !== nativeEvent.pointerId) {
          return;
        }

        finishDraggingRef.current();
      };
      const blurListener = () => {
        finishDraggingRef.current();
      };

      dragListenersRef.current = {
        move: moveListener,
        up: upListener,
        cancel: cancelListener,
        lostCapture: lostCaptureListener,
        blur: blurListener,
        target: event.currentTarget,
      };

      window.addEventListener('pointermove', moveListener, { passive: false });
      window.addEventListener('pointerup', upListener);
      window.addEventListener('pointercancel', cancelListener);
      window.addEventListener('blur', blurListener);
      event.currentTarget.addEventListener('lostpointercapture', lostCaptureListener);
    },
    [
      handleWindowPointerMove,
      isResizing,
      viewportBucket,
      widgetLayout.headerLeft,
      widgetLayout.headerTop,
    ]
  );

  const handleDragHandleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const clampWidgetSize = useCallback(
    (nextSize: WidgetSize) => {
      const nextLayout = getWidgetLayout(
        headerPosition,
        resolveViewportSize(),
        true,
        widgetState.openDirection,
        nextSize,
        false
      );

      return {
        width: nextLayout.width,
        height: nextLayout.height,
      };
    },
    [headerPosition, widgetState.openDirection]
  );

  const commitResizedSize = useCallback((nextSize: WidgetSize) => {
    setWidgetSize(nextSize);
    pendingSizeRef.current = null;
    resizeFrameRef.current = null;
  }, []);

  const scheduleResizedSize = useCallback(
    (nextSize: WidgetSize) => {
      pendingSizeRef.current = nextSize;

      if (resizeFrameRef.current !== null) {
        return;
      }

      resizeFrameRef.current = window.requestAnimationFrame(() => {
        if (pendingSizeRef.current) {
          commitResizedSize(pendingSizeRef.current);
        }
      });
    },
    [commitResizedSize]
  );

  const finishResizing = useCallback(() => {
    const activeResizeState = resizeStateRef.current;

    if (resizeFrameRef.current !== null) {
      window.cancelAnimationFrame(resizeFrameRef.current);
      resizeFrameRef.current = null;
    }

    if (pendingSizeRef.current) {
      setWidgetSize(pendingSizeRef.current);
      pendingSizeRef.current = null;
    }

    if (resizeListenersRef.current) {
      window.removeEventListener('pointermove', resizeListenersRef.current.move);
      window.removeEventListener('pointerup', resizeListenersRef.current.up);
      window.removeEventListener('pointercancel', resizeListenersRef.current.cancel);
      window.removeEventListener('blur', resizeListenersRef.current.blur);
      resizeListenersRef.current.target.removeEventListener(
        'lostpointercapture',
        resizeListenersRef.current.lostCapture
      );
      resizeListenersRef.current = null;
    }

    resizeStateRef.current = null;

    if (activeResizeState) {
      releasePointer(activeResizeState.pointerTarget, activeResizeState.pointerId);
    }

    setIsResizing(false);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, []);

  finishResizingRef.current = finishResizing;

  const handleWindowResizePointerMove = useCallback(
    (event: PointerEvent) => {
      const resizeState = resizeStateRef.current;

      if (!resizeState || event.pointerId !== resizeState.pointerId) {
        return;
      }

      if (event.buttons === 0) {
        finishResizingRef.current();
        return;
      }

      event.preventDefault();

      const deltaX = event.clientX - resizeState.originX;
      const deltaY = event.clientY - resizeState.originY;
      const nextHeight =
        resizeState.openDirection === 'up'
          ? resizeState.originHeight - deltaY
          : resizeState.originHeight + deltaY;

      scheduleResizedSize(
        clampWidgetSize({
          width: resizeState.originWidth + deltaX,
          height: nextHeight,
        })
      );
    },
    [clampWidgetSize, scheduleResizedSize]
  );

  const handleResizePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (
        event.button !== 0 ||
        viewportBucket !== 'desktop' ||
        widgetState.isMaximized ||
        !widgetState.isExpanded
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      finishResizingRef.current();

      resizeStateRef.current = {
        pointerId: event.pointerId,
        pointerTarget: event.currentTarget,
        originWidth: widgetLayout.width,
        originHeight: widgetLayout.height,
        originX: event.clientX,
        originY: event.clientY,
        openDirection: widgetLayout.openDirection,
      };

      capturePointer(event.currentTarget, event.pointerId);

      setIsResizing(true);
      document.body.style.userSelect = 'none';
      document.body.style.cursor =
        widgetLayout.openDirection === 'up' ? 'nesw-resize' : 'nwse-resize';

      const moveListener = (nativeEvent: PointerEvent) => {
        handleWindowResizePointerMove(nativeEvent);
      };
      const upListener = (nativeEvent: PointerEvent) => {
        if (resizeStateRef.current?.pointerId === nativeEvent.pointerId) {
          finishResizingRef.current();
        }
      };
      const cancelListener = (nativeEvent: PointerEvent) => {
        if (resizeStateRef.current?.pointerId === nativeEvent.pointerId) {
          finishResizingRef.current();
        }
      };
      const lostCaptureListener = (nativeEvent: PointerEvent) => {
        if (resizeStateRef.current?.pointerId === nativeEvent.pointerId) {
          finishResizingRef.current();
        }
      };
      const blurListener = () => {
        finishResizingRef.current();
      };

      resizeListenersRef.current = {
        move: moveListener,
        up: upListener,
        cancel: cancelListener,
        lostCapture: lostCaptureListener,
        blur: blurListener,
        target: event.currentTarget,
      };

      window.addEventListener('pointermove', moveListener, { passive: false });
      window.addEventListener('pointerup', upListener);
      window.addEventListener('pointercancel', cancelListener);
      window.addEventListener('blur', blurListener);
      event.currentTarget.addEventListener('lostpointercapture', lostCaptureListener);
    },
    [
      handleWindowResizePointerMove,
      viewportBucket,
      widgetLayout.height,
      widgetLayout.openDirection,
      widgetLayout.width,
      widgetState.isExpanded,
      widgetState.isMaximized,
    ]
  );

  const handleResizeKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (viewportBucket !== 'desktop' || widgetState.isMaximized) {
        return;
      }

      const sizeDelta = {
        ArrowLeft: { width: -ICOMX_RESIZE_STEP, height: 0 },
        ArrowRight: { width: ICOMX_RESIZE_STEP, height: 0 },
        ArrowUp: { width: 0, height: -ICOMX_RESIZE_STEP },
        ArrowDown: { width: 0, height: ICOMX_RESIZE_STEP },
      }[event.key];

      if (!sizeDelta) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setWidgetSize((currentSize) =>
        clampWidgetSize({
          width: currentSize.width + sizeDelta.width,
          height: currentSize.height + sizeDelta.height,
        })
      );
    },
    [clampWidgetSize, viewportBucket, widgetState.isMaximized]
  );

  const handleControlClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  const handleHeaderClick = useCallback(() => {
    if (Date.now() < suppressToggleUntilRef.current) {
      return;
    }

    toggleExpanded();
  }, [toggleExpanded]);

  useEffect(
    () => () => {
      activeRuntime.current = { key: null };
      clearQueuedLaunchTimeout();
      finishDraggingRef.current();
      finishResizingRef.current();
    },
    [clearQueuedLaunchTimeout]
  );

  useEffect(() => {
    clearQueuedLaunchTimeout();
    finishDraggingRef.current();
    finishResizingRef.current();
    updateWidgetState({
      isExpanded: false,
      isMaximized: false,
      activeIntent: null,
      pendingContactUuid: null,
      pendingSelectedTelecom: null,
    });
  }, [runtime, clearQueuedLaunchTimeout, updateWidgetState]);

  const contextValue = useMemo(
    () => ({
      isEnabled,
      isLoading,
      isLaunching: isEnabled && (!!widgetState.activeIntent || !!widgetState.pendingContactUuid),
      openResourceConversation,
    }),
    [
      isEnabled,
      isLoading,
      openResourceConversation,
      widgetState.activeIntent,
      widgetState.pendingContactUuid,
    ]
  );

  return (
    <IcomxChatContext.Provider value={contextValue}>
      {children}

      {isEnabled && (
        <Paper
          key={runtimeKey}
          data-testid="icomx-global-widget"
          data-open-direction={widgetLayout.openDirection}
          data-maximized={widgetState.isMaximized ? 'true' : 'false'}
          data-resizing={isResizing ? 'true' : 'false'}
          elevation={isDragging || isResizing ? 16 : widgetState.isExpanded ? 12 : 8}
          style={{
            width: `${widgetLayout.width}px`,
            height: `${widgetLayout.height}px`,
            transform: `translate3d(${widgetLayout.headerLeft}px, ${widgetLayout.panelTop}px, 0)`,
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 2000,
            display: 'flex',
            flexDirection:
              widgetState.isExpanded && widgetLayout.openDirection === 'up'
                ? 'column-reverse'
                : 'column',
            overflow: 'hidden',
            borderRadius: 2,
            willChange: isDragging || isResizing ? 'transform, width, height' : 'auto',
            transition: isDragging || isResizing
              ? 'box-shadow 120ms ease'
              : 'transform 140ms ease, width 140ms ease, height 140ms ease, box-shadow 140ms ease',
          }}
        >
          <Box
            data-testid="icomx-global-widget-header"
            data-dragging={isDragging ? 'true' : 'false'}
            onClick={handleHeaderClick}
            sx={{
              minHeight: ICOMX_HEADER_HEIGHT,
              px: 1.5,
              py: 1.25,
              color: 'common.white',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'default',
              userSelect: 'none',
              boxShadow: isDragging ? 'inset 0 0 0 1px rgba(255,255,255,0.28)' : 'none',
              filter: isDragging ? 'brightness(0.96)' : 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                data-testid="icomx-global-widget-drag-handle"
                onPointerDown={handleDragHandlePointerDown}
                onClick={handleDragHandleClick}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor:
                    viewportBucket === 'desktop'
                      ? isDragging
                        ? 'grabbing'
                        : 'grab'
                      : 'default',
                  mr: 0.25,
                  touchAction: 'none',
                }}
              >
                <DragIndicatorIcon fontSize="small" sx={{ opacity: 0.72 }} />
              </Box>
              <IcomxCommunicationIcon fontSize="small" />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {widgetState.isExpanded && viewportBucket === 'desktop' ? (
                <Tooltip
                  title={widgetState.isMaximized ? 'Restore iComX panel' : 'Maximize iComX panel'}
                >
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={(event) => {
                      handleControlClick(event);
                      toggleMaximized();
                    }}
                    aria-label={
                      widgetState.isMaximized ? 'Restore iComX panel' : 'Maximize iComX panel'
                    }
                  >
                    {widgetState.isMaximized ? (
                      <CloseFullscreenIcon fontSize="small" />
                    ) : (
                      <OpenInFullIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip
                title={widgetState.isExpanded ? 'Minimize iComX panel' : 'Expand iComX panel'}
              >
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={(event) => {
                    handleControlClick(event);
                    toggleExpanded();
                  }}
                  aria-label={
                    widgetState.isExpanded ? 'Minimize iComX panel' : 'Expand iComX panel'
                  }
                  aria-expanded={widgetState.isExpanded}
                >
                  {widgetState.isExpanded ? (
                    <KeyboardArrowDownIcon fontSize="small" />
                  ) : (
                    <KeyboardArrowUpIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Divider />

          <Box
            data-testid="icomx-global-panel-body"
            sx={{
              flex: 1,
              display: widgetState.isExpanded ? 'flex' : 'none',
              flexDirection: 'column',
              minHeight: 0,
              backgroundColor: '#f4f6f8',
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <iframe
                ref={iframeRef}
                title="iComX chat"
                src={iframeSrc!}
                onLoad={handleIframeLoad}
                sandbox={ICOMX_IFRAME_SANDBOX}
                allow="clipboard-read; clipboard-write"
                referrerPolicy="strict-origin-when-cross-origin"
                style={{ border: 0, width: '100%', height: '100%', display: 'block' }}
              />
            </Box>
          </Box>

          {widgetState.isExpanded &&
          !widgetState.isMaximized &&
          viewportBucket === 'desktop' ? (
            <Box
              data-testid="icomx-global-widget-resize-handle"
              role="button"
              tabIndex={0}
              aria-label="Resize iComX panel"
              onPointerDown={handleResizePointerDown}
              onKeyDown={handleResizeKeyDown}
              sx={{
                position: 'absolute',
                zIndex: 3,
                right: 0,
                top: widgetLayout.openDirection === 'up' ? 0 : 'auto',
                bottom: widgetLayout.openDirection === 'down' ? 0 : 'auto',
                width: 24,
                height: 24,
                cursor: widgetLayout.openDirection === 'up' ? 'nesw-resize' : 'nwse-resize',
                touchAction: 'none',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: 4,
                  top: widgetLayout.openDirection === 'up' ? 4 : 'auto',
                  bottom: widgetLayout.openDirection === 'down' ? 4 : 'auto',
                  width: 9,
                  height: 9,
                  borderRight: '2px solid rgba(33,43,54,0.42)',
                  borderTop:
                    widgetLayout.openDirection === 'up'
                      ? '2px solid rgba(33,43,54,0.42)'
                      : 'none',
                  borderBottom:
                    widgetLayout.openDirection === 'down'
                      ? '2px solid rgba(33,43,54,0.42)'
                      : 'none',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: -2,
                },
              }}
            />
          ) : null}
        </Paper>
      )}
    </IcomxChatContext.Provider>
  );
}
